import { motion } from 'framer-motion';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useState, useEffect, useRef } from 'react';
import { slideDownFromTop } from '../../../shared/helpers/animation';
import { useAppSelector } from '../../../shared/store/store';
import useProfileService from '../../../shared/services/useProfileService';
import { FilterMatchMode } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import Button from '../../../shared/components/Button';
import useCSVService from '../../../shared/services/useCSVService';
import { Profile } from '../../../shared/types';
import NewProfileForm from '../../components/NewProfileForm';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';

const AdminDashboard = () => {
  //State
  const { userProfile, allProfiles } = useAppSelector((state) => state.profile);
  const [activeUser, setActiveUser] = useState<any>(null);
  const [filters, setFilters] = useState<any>(null);
  const [searchText, setSearchText] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);
  const [expandedRows, setExpandedRows] = useState<any>(null);
  const { downloadUserWatchDataCSV, downloadAllWatchDataCSV } = useCSVService();
  const [downloading, setDownloading] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  //Hooks
  const { getAllProfiles, setProfileEnabled } = useProfileService();

  //useEffects
  useEffect(() => {
    getAllProfiles();
  }, []);
  useEffect(() => {
    initFilters();
  }, []);

  //Functions
  const onRowCollapse = (event: any) => {
    setActiveUser(null);
  };

  const rowExpansionTemplate = (data: Profile) => {
    return (
      <div className="ml-20 flex items-center gap-x-24">
        <div className="flex flex-col gap-y-4">
          <h2>
            <span className="font-bold">Organization name: </span>
            {data.organization}
          </h2>
          <h2>
            <>
              <span className="font-bold">Date joined: </span>
              {data.createdOn}
            </>
          </h2>
        </div>
        <span className="flex gap-4">
          <Button
            label="Download Data"
            className="p-button-outlined"
            onClick={() => handleDownloadProfile(data)}
            loading={downloading}
          />
          <Button
            label={data.enabled ? "Disable" : "Enable"}
            className="p-button-outlined"
            onClick={() => handleToggleEnable(data)}
            loading={downloading}
            disabled={data.email === userProfile?.email}
          />
        </span>
      </div>
    );
  };

  const enabledTemplate = (row:Profile) => {
    return row.enabled ? <i className="pi pi-check-circle"/> : <i className="pi pi-ban"/>;
  };

  //Functions
  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters['global'].value = value;

    setFilters(_filters);
    setSearchText(value);
  };

  const initFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setSearchText('');
  };

  const clearSearch = () => {
    initFilters();
  };

  const handleDownloadAll = async () => {
    try {
      setDownloading(true);
      await downloadAllWatchDataCSV();
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadProfile = async (profile: Profile) => {
    try {
      setDownloading(true);
      await downloadUserWatchDataCSV(profile);
    } finally {
      setDownloading(false);
    }
  };

  const handleAddAccount = () => {
    setAddAccountOpen(true);
  };

  const handleToggleEnable = async (profile:Profile) => {
    await setProfileEnabled(profile.email, !profile.enabled)
    await getAllProfiles(); // force rerender
  }

  return (
    <div>
      <header className="flex w-full justify-between p-8">
        <div>
          <motion.h1
            className="text-3xl font-bold"
            variants={slideDownFromTop}
            initial="hidden"
            animate="visible"
          >
            Admin Dashboard
          </motion.h1>
          <p className=" font-light text-neutral-500">
            {userProfile?.firstName} {userProfile?.lastName}
          </p>
        </div>
      </header>
      <div className="card ml-8 w-4/5">
        <div className="flex flex-wrap gap-4 w-full items-center justify-between  mb-6">
          <span className="flex gap-x-4 content-center">
            <span
              className={`p-input-icon-left ${
                searchText !== '' && 'p-input-icon-right'
              }`}
            >
              <i className="pi pi-search" />
              <InputText
                value={searchText}
                onChange={onGlobalFilterChange}
                placeholder="Search"
              />
              {searchText !== '' && (
                <motion.i
                  className="pi pi-times cursor-pointer"
                  onClick={() => clearSearch()}
                />
              )}
            </span>

            <span className="flex items-center gap-2 text-lg content-center">
              <span>Show all</span>
              <InputSwitch 
                checked={showDisabled} 
                onChange={e => setShowDisabled(e.value)}
                tooltip="Show all profiles, including disabled"
              />
            </span>
          </span>

          <span className="flex gap-x-4">
            <Button
              label="Add Account"
              icon="pi pi-user-plus"
              onClick={() => handleAddAccount()}
            />
            <Button
              label="Download all data"
              onClick={() => handleDownloadAll()}
              loading={downloading}
            />
          </span>
        </div>
        <DataTable
          value={allProfiles.filter(p => p.enabled || showDisabled)}
          expandedRows={expandedRows}
          responsiveLayout="scroll"
          stripedRows
          globalFilterFields={[
            'firstName',
            'lastName',
            'email',
            'organization',
          ]}
          filters={filters}
          onRowCollapse={onRowCollapse}
          dataKey="id"
          onRowToggle={(e) => setExpandedRows(e.data)}
          rowClassName={(data) => { 
            return data.enabled ? "" : "!text-gray-300";
          }}
          rowExpansionTemplate={rowExpansionTemplate}
          sortField="lastLogin"
          sortOrder={1}
        >
          <Column expander style={{ width: '1em' }} />
          <Column field="firstName" header="First" sortable></Column>
          <Column field="lastName" header="Last" sortable></Column>
          <Column field="email" header="Email"></Column>
          <Column field="lastLogin" header="Last login" sortable></Column>
          {/*<Column field="enabled" header="Enabled" align="center" body={enabledTemplate} ></Column>*/}
        </DataTable>
      </div>

      <Dialog
          visible={addAccountOpen}
          onHide={() => { setAddAccountOpen(false); }}
          showHeader={false}
          className=""
        >
        <NewProfileForm handleCloseModal={ 
          () => { 
            setAddAccountOpen(false);
            getAllProfiles();
          }
        }/>
      </Dialog>
    </div>

    
  );
};

export default AdminDashboard;
